import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Warehouse, Truck, Package, MapPin, Activity, Users, Store, Boxes,
  ChevronRight,
} from "lucide-react";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

// Stage 4E (Feb 2026): Operations Admin Dashboard
// Read-only view of stock + orders + tracking. Full assign control.

export default function OpsAdminDashboard() {
  const { lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();

  const cards = [
    {
      id: "assign-stores",
      title: lang === "ta" ? "கடை ஒதுக்கீடு" : "Assign Stores",
      sub: lang === "ta" ? "நிர்வாக + சேல்ஸ்மென்" : "Managers + salesmen",
      icon: Store, color: "from-[#0055FF] to-[#5856D6]",
      onClick: () => nav("/ops/assign"),
      featured: true,
    },
    {
      id: "stock-tracking",
      title: lang === "ta" ? "ஸ்டாக் கண்காணிப்பு" : "Stock Tracking",
      sub: lang === "ta" ? "டிப்போ + வழியில்" : "Depot + on-road",
      icon: Warehouse, color: "from-[#0A1A3F] to-[#1F3F8F]",
      onClick: () => nav("/admin/stock"),
    },
    {
      id: "orders",
      title: lang === "ta" ? "ஆர்டர்கள்" : "Orders",
      sub: lang === "ta" ? "காத்திருக்கும் + டெலிவரி" : "Pending + delivered",
      icon: Package, color: "from-[#FF9500] to-[#FF6B00]",
      onClick: () => nav("/admin/sales"),
    },
    {
      id: "live-tracking",
      title: lang === "ta" ? "லைவ் ட்ராக்கிங்" : "Live Tracking",
      sub: lang === "ta" ? "GPS + பாதை வரலாறு" : "GPS + route history",
      icon: MapPin, color: "from-[#34C759] to-[#2BAE4D]",
      onClick: () => nav("/management/live-tracking"),
    },
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F7] page-slide-in pb-12">
      <TopBar title={lang === "ta" ? "செயல்பாடுகள்" : "Operations"} />
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6 fade-up">
        {/* HERO */}
        <div className="bg-gradient-to-br from-[#0A1A3F] via-[#0055FF] to-[#5856D6] text-white rounded-3xl p-6 sm:p-8 shadow-xl" data-testid="ops-hero">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 grid place-items-center shrink-0">
              <Activity className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider opacity-80 font-bold">{lang === "ta" ? "வணக்கம்" : "Welcome"}</div>
              <h1 className="heading-font text-3xl sm:text-4xl font-bold mt-1">{user?.name || "Ops Admin"}</h1>
              <p className="opacity-90 mt-2 text-sm sm:text-base">
                {lang === "ta"
                  ? "செயல்பாட்டு கட்டுப்பாடு — ஸ்டாக் கண்காணி, கடைகள் ஒதுக்கு, GPS பார், ஆர்டர்களை கண்காணி."
                  : "Operations control — track stock, assign shops, monitor GPS, watch orders."}
              </p>
            </div>
          </div>
        </div>

        {/* MAIN CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="ops-cards">
          {cards.map(c => {
            const Icon = c.icon;
            return (
              <button key={c.id} onClick={c.onClick} data-testid={`ops-card-${c.id}`}
                className={`group relative overflow-hidden rounded-3xl p-6 text-left text-white bg-gradient-to-br ${c.color} shadow-xl hover:shadow-2xl transition active:scale-[0.99] ${c.featured ? "sm:col-span-2" : ""}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="w-12 h-12 rounded-2xl bg-white/15 grid place-items-center mb-4">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="heading-font text-2xl sm:text-3xl font-bold">{c.title}</div>
                    <div className="text-sm opacity-90 mt-1">{c.sub}</div>
                  </div>
                  <ChevronRight className="w-6 h-6 opacity-60 group-hover:translate-x-1 transition" />
                </div>
              </button>
            );
          })}
        </div>

        {/* RESTRICTIONS NOTICE */}
        <div className="bg-white rounded-2xl p-4 soft-shadow text-xs text-[#86868B]" data-testid="ops-restrictions">
          <div className="font-bold text-[#1D1D1F] uppercase tracking-wider text-[10px] mb-1">
            {lang === "ta" ? "பார்வை மட்டும்" : "View-only access"}
          </div>
          {lang === "ta"
            ? "விற்பனை திருத்தம், ஸ்டாக் மதிப்பு மாற்றம், செயல்திறன் மாற்றம் — செய்ய முடியாது."
            : "Cannot edit sales, modify stock values, or change performance ranking. All actions are audit-logged."}
        </div>
      </div>
    </div>
  );
}
